import { getImageUrl, normalizeMediaPath } from '@/lib/utils';
import { buildRelatedNewsLightningSvgHtml } from '@/lib/related-news-icons';

export const RELATED_NEWS_LABEL = 'İLGİLİ HABER';

export type RelatedNewsItem = {
  articleId: number;
  title: string;
  slug: string;
  categorySlug: string;
  categoryTitle: string;
  imagePath: string;
};

export function buildArticleHref(item: RelatedNewsItem): string {
  return `/${item.categorySlug}/${item.slug}-${item.articleId}`;
}

export function dedupeRelatedNewsItems(items: RelatedNewsItem[]): RelatedNewsItem[] {
  const seen = new Set<number>();
  const result: RelatedNewsItem[] = [];
  for (const item of items) {
    if (!item.articleId || seen.has(item.articleId)) continue;
    seen.add(item.articleId);
    result.push(item);
  }
  return result;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function parseArticleIdFromElement(el: Element): number {
  const dataId = el.getAttribute('data-related-news-id');
  if (dataId) return Number(dataId) || 0;

  const idAttr = el.getAttribute('id') || '';
  const relatedMatch = idAttr.match(/^related_(\d+)$/i);
  if (relatedMatch) return Number(relatedMatch[1]) || 0;

  return Number(idAttr) || 0;
}

function parseArticleIdFromOpenTag(openTag: string): number {
  const dataId = getAttrFromTag(openTag, 'data-related-news-id');
  if (dataId) return Number(dataId) || 0;

  const idAttr = getAttrFromTag(openTag, 'id');
  const relatedMatch = idAttr.match(/^related_(\d+)$/i);
  if (relatedMatch) return Number(relatedMatch[1]) || 0;

  return Number(idAttr) || 0;
}

function parseSlugFromArticleHref(
  href: string,
): { slug: string; categorySlug: string } | null {
  const clean = href.replace(/^https?:\/\/[^/]+/i, '');
  const match = clean.match(/\/([^/?#]+)\/([^/?#]+)-(\d+)\/?(?:\?|#|$)/);
  if (!match) return null;
  return { categorySlug: match[1], slug: match[2] };
}

function replaceElementWithHtml(doc: Document, element: Element, html: string): void {
  const tmp = parseHtml(html);
  if (!tmp?.body.firstChild) return;

  const fragment = doc.createDocumentFragment();
  while (tmp.body.firstChild) {
    fragment.appendChild(tmp.body.firstChild);
  }
  element.replaceWith(fragment);
}

/** Kayıt: kompakt `<related-news id="...">` şablonu */
export function buildRelatedNewsTemplateHtml(items: RelatedNewsItem[]): string {
  const unique = dedupeRelatedNewsItems(items);
  if (unique.length === 0) return '';

  const tags = unique
    .map((item) => {
      const image = normalizeMediaPath(item.imagePath);
      const attrs = [
        `id="${item.articleId}"`,
        `data-slug="${escapeAttr(item.slug)}"`,
        `data-category-slug="${escapeAttr(item.categorySlug)}"`,
        `data-category="${escapeAttr(item.categoryTitle)}"`,
        `data-title="${escapeAttr(item.title)}"`,
      ];
      if (image) {
        attrs.push(`data-image="${escapeAttr(image)}"`);
      }
      return `<related-news ${attrs.join(' ')}></related-news>`;
    })
    .join('');

  return `<aside class="related-news-block" data-format="template">${tags}</aside>`;
}

/** Görüntüleme: legacy `content-card` yapısı (site + editör) */
export function buildRelatedNewsCardHtml(
  item: RelatedNewsItem,
  options?: { preview?: boolean },
): string {
  const href = buildArticleHref(item);
  const relativeImage = normalizeMediaPath(item.imagePath);
  const imageSrc = relativeImage ? getImageUrl(relativeImage) : '';
  const preview = options?.preview ?? false;
  const linkExtra = preview ? ' tabindex="-1" onclick="return false"' : '';
  const dataAttrs = [
    `data-related-news-id="${item.articleId}"`,
    `data-related-news-slug="${escapeAttr(item.slug)}"`,
    `data-related-news-category-slug="${escapeAttr(item.categorySlug)}"`,
    `data-related-news-category="${escapeAttr(item.categoryTitle)}"`,
    `data-related-news-title="${escapeAttr(item.title)}"`,
  ];
  if (relativeImage) {
    dataAttrs.push(`data-related-news-image="${escapeAttr(relativeImage)}"`);
  }

  const imageHtml = imageSrc
    ? `<a href="${escapeAttr(href)}"${linkExtra}><img class="content-card-image lazy" src="${escapeAttr(imageSrc)}" alt="${escapeAttr(item.title)}" loading="lazy" /></a>`
    : '';

  return `<div id="related_${item.articleId}" class="content-card noneditable related-news-item" ${dataAttrs.join(' ')}>${imageHtml}<div class="content-card-detail"><div class="content-card-category">${buildRelatedNewsLightningSvgHtml()}<span class="content-card-category-text">${escapeText(RELATED_NEWS_LABEL)}</span></div><h3 class="content-card-title"><a title="${escapeAttr(item.title)}" href="${escapeAttr(href)}"${linkExtra}>${escapeText(item.title)}</a></h3></div></div>`;
}

export function buildRelatedNewsBlockDisplayHtml(
  items: RelatedNewsItem[],
  options?: { preview?: boolean },
): string {
  const unique = dedupeRelatedNewsItems(items);
  if (unique.length === 0) return '';
  return unique.map((item) => buildRelatedNewsCardHtml(item, options)).join('');
}

export function parseRelatedNewsElement(el: Element): RelatedNewsItem | null {
  const articleId = parseArticleIdFromElement(el);
  const title =
    el.getAttribute('data-title') ||
    el.getAttribute('data-related-news-title') ||
    el.querySelector('.related-news-card__title')?.textContent?.trim() ||
    el.querySelector('.content-card-title')?.textContent?.trim() ||
    el.querySelector('.content-card-title a')?.textContent?.trim() ||
    '';
  if (!articleId || !title) return null;

  const href =
    el.querySelector('.content-card-title a')?.getAttribute('href') ||
    el.querySelector('.content-card > a')?.getAttribute('href') ||
    el.querySelector('.related-news-card__title a')?.getAttribute('href') ||
    '';
  const fromHref = href ? parseSlugFromArticleHref(href) : null;

  const slug =
    el.getAttribute('data-slug') ||
    el.getAttribute('data-related-news-slug') ||
    fromHref?.slug ||
    '';
  const categorySlug =
    el.getAttribute('data-category-slug') ||
    el.getAttribute('data-related-news-category-slug') ||
    fromHref?.categorySlug ||
    'haber';
  const categoryTitle =
    el.getAttribute('data-category') ||
    el.getAttribute('data-related-news-category') ||
    el.querySelector('.content-card-category-text')?.textContent?.trim() ||
    el.querySelector('.content-card-category')?.textContent?.trim() ||
    'Haber';
  const imagePath = normalizeMediaPath(
    el.getAttribute('data-image') ||
      el.getAttribute('data-related-news-image') ||
      el.querySelector('.related-news-card__image')?.getAttribute('src') ||
      el.querySelector('.content-card-image')?.getAttribute('src') ||
      '',
  );

  return {
    articleId,
    title,
    slug,
    categorySlug,
    categoryTitle,
    imagePath,
  };
}

export function parseRelatedNewsBlockElement(block: Element): RelatedNewsItem[] {
  const fromTags = Array.from(block.querySelectorAll('related-news'))
    .map(parseRelatedNewsElement)
    .filter((item): item is RelatedNewsItem => item !== null);
  if (fromTags.length > 0) return dedupeRelatedNewsItems(fromTags);

  const fromCards = Array.from(
    block.querySelectorAll('.related-news-item, .related-news-card, .content-card'),
  )
    .map(parseRelatedNewsElement)
    .filter((item): item is RelatedNewsItem => item !== null);
  return dedupeRelatedNewsItems(fromCards);
}

function parseHtml(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null;
  return new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
}

function decodeHtmlEntities(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function getAttrFromTag(tag: string, name: string): string {
  const patterns = [
    new RegExp(`\\b${name}="([^"]*)"`, 'i'),
    new RegExp(`\\b${name}='([^']*)'`, 'i'),
  ];
  for (const re of patterns) {
    const m = tag.match(re);
    if (m) return decodeHtmlEntities(m[1]);
  }
  return '';
}

function parseRelatedNewsTagFromString(tag: string): RelatedNewsItem | null {
  const articleId = Number(
    getAttrFromTag(tag, 'id') || getAttrFromTag(tag, 'data-related-news-id'),
  );
  const title =
    getAttrFromTag(tag, 'data-title') ||
    getAttrFromTag(tag, 'data-related-news-title');
  if (!articleId || !title) return null;

  return {
    articleId,
    title,
    slug:
      getAttrFromTag(tag, 'data-slug') ||
      getAttrFromTag(tag, 'data-related-news-slug'),
    categorySlug:
      getAttrFromTag(tag, 'data-category-slug') ||
      getAttrFromTag(tag, 'data-related-news-category-slug') ||
      'haber',
    categoryTitle:
      getAttrFromTag(tag, 'data-category') ||
      getAttrFromTag(tag, 'data-related-news-category') ||
      'Haber',
    imagePath: normalizeMediaPath(
      getAttrFromTag(tag, 'data-image') ||
        getAttrFromTag(tag, 'data-related-news-image'),
    ),
  };
}

function parseRelatedNewsCardFromString(cardHtml: string): RelatedNewsItem | null {
  const openTag =
    cardHtml.match(/^<(?:article|aside|div)\b[^>]*>/i)?.[0] || cardHtml;
  const articleId = parseArticleIdFromOpenTag(openTag);
  let title = getAttrFromTag(openTag, 'data-related-news-title');
  if (!title) {
    const titleMatch = cardHtml.match(
      /class="[^"]*(?:related-news-card__title|content-card-title)[^"]*"[^>]*>\s*(?:<a[^>]*>)?([\s\S]*?)(?:<\/a>)?\s*<\/(?:div|h\d)>/i,
    );
    title = titleMatch ? decodeHtmlEntities(titleMatch[1].trim()) : '';
  }
  if (!articleId || !title) return null;

  let imagePath = normalizeMediaPath(
    getAttrFromTag(openTag, 'data-related-news-image'),
  );
  if (!imagePath) {
    const imgMatch = cardHtml.match(
      /class="[^"]*(?:related-news-card__image|content-card-image)[^"]*"[^>]*\bsrc="([^"]+)"/i,
    );
    if (imgMatch) imagePath = normalizeMediaPath(decodeHtmlEntities(imgMatch[1]));
  }

  const hrefMatch = cardHtml.match(
    /class="[^"]*content-card-title[^"]*"[^>]*>\s*<a[^>]*\bhref="([^"]+)"/i,
  );
  const fromHref = hrefMatch
    ? parseSlugFromArticleHref(decodeHtmlEntities(hrefMatch[1]))
    : null;

  return {
    articleId,
    title,
    slug:
      getAttrFromTag(openTag, 'data-related-news-slug') ||
      getAttrFromTag(openTag, 'data-slug') ||
      fromHref?.slug ||
      '',
    categorySlug:
      getAttrFromTag(openTag, 'data-related-news-category-slug') ||
      getAttrFromTag(openTag, 'data-category-slug') ||
      fromHref?.categorySlug ||
      'haber',
    categoryTitle:
      getAttrFromTag(openTag, 'data-related-news-category') ||
      getAttrFromTag(openTag, 'data-category') ||
      'Haber',
    imagePath,
  };
}

function parseRelatedNewsBlockFromString(innerHtml: string): RelatedNewsItem[] {
  const items: RelatedNewsItem[] = [];

  const tagRegex = /<related-news\b([^>]*)>\s*(?:<\/related-news>)?/gi;
  let tagMatch: RegExpExecArray | null;
  while ((tagMatch = tagRegex.exec(innerHtml)) !== null) {
    const item = parseRelatedNewsTagFromString(`<related-news${tagMatch[1]}>`);
    if (item) items.push(item);
  }
  if (items.length > 0) return dedupeRelatedNewsItems(items);

  const cardRegex =
    /<(?:article|aside|div)\b[^>]*\b(?:related-news-item|related-news-card|content-card)\b[^>]*>[\s\S]*?<\/(?:article|aside|div)>/gi;
  let cardMatch: RegExpExecArray | null;
  while ((cardMatch = cardRegex.exec(innerHtml)) !== null) {
    const item = parseRelatedNewsCardFromString(cardMatch[0]);
    if (item) items.push(item);
  }

  return dedupeRelatedNewsItems(items);
}

function expandRelatedNewsWithRegex(html: string): string {
  return html.replace(
    /<aside\b[^>]*\brelated-news-block\b[^>]*>([\s\S]*?)<\/aside>/gi,
    (blockMatch, inner) => {
      const items = parseRelatedNewsBlockFromString(inner);
      if (items.length === 0) return blockMatch;
      return buildRelatedNewsBlockDisplayHtml(items);
    },
  );
}

function compactRelatedNewsWithRegex(html: string): string {
  return html.replace(
    /<aside\b[^>]*\brelated-news-block\b[^>]*>([\s\S]*?)<\/aside>/gi,
    (blockMatch, inner) => {
      const items = parseRelatedNewsBlockFromString(inner);
      if (items.length === 0) return blockMatch;
      return buildRelatedNewsTemplateHtml(items);
    },
  );
}

/** Şablon veya eski kartları görüntüleme HTML'ine çevirir (site + editör) */
export function expandRelatedNewsInContentHtml(html: string): string {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return expandRelatedNewsWithRegex(html);

  doc.body.querySelectorAll('aside.related-news-block').forEach((block) => {
    const items = parseRelatedNewsBlockElement(block);
    if (items.length === 0) return;
    replaceElementWithHtml(
      doc,
      block,
      buildRelatedNewsBlockDisplayHtml(items),
    );
  });

  doc.body.querySelectorAll('div.content-card, aside.content-card').forEach((card) => {
    if (card.closest('aside.related-news-block')) return;
    const item = parseRelatedNewsElement(card);
    if (!item) return;
    replaceElementWithHtml(doc, card, buildRelatedNewsCardHtml(item));
  });

  doc.body
    .querySelectorAll('article.related-news-card, article.related-news-item')
    .forEach((card) => {
      if (card.closest('aside.related-news-block')) return;
      const item = parseRelatedNewsElement(card);
      if (!item) return;
      replaceElementWithHtml(doc, card, buildRelatedNewsCardHtml(item));
    });

  return doc.body.innerHTML;
}

/** Kayıt öncesi kompakt şablona indirger */
export function compactRelatedNewsInContentHtml(html: string): string {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return compactRelatedNewsWithRegex(html);

  doc.body.querySelectorAll('aside.related-news-block').forEach((block) => {
    const items = parseRelatedNewsBlockElement(block);
    if (items.length === 0) return;
    const compact = buildRelatedNewsTemplateHtml(items);
    const tmp = parseHtml(compact);
    const newBlock = tmp?.body.firstElementChild;
    if (newBlock) {
      block.replaceWith(newBlock);
    }
  });

  doc.body.querySelectorAll('div.content-card, aside.content-card').forEach((card) => {
    if (card.closest('aside.related-news-block')) return;
    const item = parseRelatedNewsElement(card);
    if (!item) return;
    replaceElementWithHtml(doc, card, buildRelatedNewsTemplateHtml([item]));
  });

  doc.body
    .querySelectorAll('article.related-news-card, article.related-news-item')
    .forEach((card) => {
      if (card.closest('aside.related-news-block')) return;
      const item = parseRelatedNewsElement(card);
      if (!item) return;
      replaceElementWithHtml(doc, card, buildRelatedNewsTemplateHtml([item]));
    });

  return doc.body.innerHTML;
}

export function rewriteRelatedNewsImagesInHtml(
  html: string,
  mapPath: (relative: string) => string | null,
): string {
  if (!html?.trim()) return html;

  const doc = parseHtml(html);
  if (!doc) return html;

  doc.body.querySelectorAll('related-news[data-image]').forEach((el) => {
    const raw = el.getAttribute('data-image');
    if (!raw) return;
    const relative = normalizeMediaPath(raw);
    const mapped = mapPath(relative);
    if (mapped) el.setAttribute('data-image', mapped);
  });

  doc.body
    .querySelectorAll(
      '.related-news-block .related-news-card__image, .content-card .content-card-image, aside.content-card .content-card-image',
    )
    .forEach((img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:')) return;
      const relative = normalizeMediaPath(src);
      const mapped = mapPath(relative);
      if (mapped) img.setAttribute('src', mapped);
    });

  return doc.body.innerHTML;
}
